/**
 * Store Zustand — orchestration du moteur WASM et de l'état courant du jeu.
 *
 * La prochaine question est délégée au `QuestionSelector` côté Rust. Le store
 * maintient `decisionsHistory` (la source de vérité déterministe) et persiste
 * un blob opaque sur le serveur via `/api/saves/:session_id`. Au boot, si une
 * save existe pour la session, on rejoue chaque tour pour reconstruire l'état.
 */

import { create } from "zustand";
import type { WasmEngine } from "../wasm-pkg/mp_wasm";
import {
  advanceTurn,
  availableOpportunity as wasmAvailableOpportunity,
  cancelRemediation,
  createEngine,
  getEmbeddedDecisionsJson,
  getEmbeddedEuropeanInterventionsJson,
  injectRemediation,
  pendingEffects,
  playedDecisions,
  pledges as wasmPledges,
  seizeOpportunity as wasmSeizeOpportunity,
  selectorMode,
  snapshot,
  suggestedNext,
  type PendingEffect,
  type PledgeView,
  type SelectorMode,
} from "../engine/wasm";
import type { Decision, Revelation, WorldState } from "../engine/types";
import { deriveAggregates } from "../engine/types";
import type { IndicatorHistory } from "../engine/indicators";
import { emptyHistory, pushHistory } from "../engine/indicators";
import { buildTurnReport, type TurnReport } from "../engine/turn-report";
import { track, trackMandateEnded } from "../engine/tracking";
import { trafficSource } from "../engine/source";
import { clearSession, getOrCreateSessionId } from "../engine/session";
import {
  deleteSave,
  fetchSave,
  pushSave,
  type SaveBlob,
} from "../engine/saves";
import { loadTropes } from "../engine/narratives";

// R5 — Helpers extraits dans des modules dédiés (sortis de useGame.ts).
import { generateSeed, shuffleScriptedSymbolics } from "./seed-rng";
import { loadPollBaseline, persistPollBaseline } from "./poll-baseline";
import {
  INITIAL_HEADLINES,
  coerceHeadlineFeed,
  mergeEventsIntoHeadlines,
  type HeadlineEntry,
} from "./headlines";
// Re-exports pour préserver l'API publique du store (composants
// importent encore depuis "../store/useGame").
export { type HeadlineEntry, type HeadlineUrgency } from "./headlines";
import {
  ARCHETYPES,
  ARCHETYPE_CHOICE_KEY,
} from "../components/ArchetypeSelector";

/**
 * Europe S2 — paliers UE dont le déclenchement mérite un CheckpointBanner
 * solennel (équivalent visuel des BILAN T13/T30/T46). Article 7, fonds
 * gelés, mise sous tutelle MES = moments historiques du mandat.
 */
const CHECKPOINT_EU_IDS = new Set([
  "eu_article_7",
  "eu_fonds_geles",
  "eu_mes_tutelle",
]);

/**
 * Détecte un nouveau checkpoint dans le résultat de tour. Deux sources :
 * - BILAN aux jalons T13/T30/T46 (existe depuis L5a)
 * - Europe S2 : un nouveau palier UE critique apparaît dans polity
 */
function detectCheckpoint(
  resultHeadlines: { text: string; source?: string }[],
  previousInterventions: { id: string }[] | undefined,
  newInterventions:
    { id: string; headline_text: string; source: string }[] | undefined,
): HeadlineEntry | null {
  const bilan = resultHeadlines.find((h) =>
    (h.source ?? "").startsWith("BILAN ·"),
  );
  if (bilan) {
    return {
      text: bilan.text,
      source: bilan.source,
      urgency: "checkpoint",
    };
  }
  const prevIds = new Set((previousInterventions ?? []).map((iv) => iv.id));
  const freshCritical = (newInterventions ?? []).find(
    (iv) => CHECKPOINT_EU_IDS.has(iv.id) && !prevIds.has(iv.id),
  );
  if (freshCritical) {
    return {
      text: freshCritical.headline_text,
      source: `🇪🇺 ${freshCritical.source}`,
      urgency: "checkpoint",
    };
  }
  return null;
}

/** N1 — résout le chemin du JSON d'archétype selon le choix localStorage. */
function loadArchetypeFile(): string {
  try {
    const raw = localStorage.getItem(ARCHETYPE_CHOICE_KEY);
    if (raw) {
      const found = ARCHETYPES.find((a) => a.id === raw);
      if (found) return found.file;
    }
  } catch {
    /* ignore */
  }
  return ARCHETYPES[0].file;
}

export type BootStatus = "idle" | "loading" | "ready" | "error";

interface GameState {
  status: BootStatus;
  error: string | null;
  engine: WasmEngine | null;
  worldState: WorldState | null;
  lastRevelations: Revelation[];
  headlineFeed: HeadlineEntry[];
  /**
   * J2 — état du monde au moment du *dernier sondage Ifop affiché*.
   * Sert de baseline pour calculer la variation par CSP au sondage suivant
   * (auparavant le delta était toujours 0 car previousState restait null).
   * Persisté dans localStorage pour survivre au refresh.
   */
  lastPollState: WorldState | null;

  /** Identifiant de session (UUID) — posé en cookie au premier boot. */
  sessionId: string | null;
  /** Métadonnées pour la save (captées au boot). */
  planId: string;
  planVersion: string;
  archetypeId: string;
  /** N2 — seed RNG de la session (généré au boot ou hérité de la save). */
  seed: number;

  /** Tous les archétypes chargés (onboarding + symboliques). */
  decisions: Decision[];
  /** Historique des choix joués — source de vérité pour la sauvegarde. */
  decisionsHistory: string[][];
  /**
   * Historique des archétypes proposés à chaque tour affiché.
   * Sauvegardé pour figer visuellement la question présentée — garantit
   * qu'un refresh n'affiche pas une question différente du même tour.
   */
  presentedHistory: (string | null)[];

  /** Id de l'archétype suggéré par le moteur pour le prochain tour. */
  nextArchetypeId: string | null;
  /** Mode du sélecteur — utile pour l'UI (badge de phase). */
  selectorPhase: SelectorMode;

  indicatorHistory: IndicatorHistory;
  activeIndicatorId: string | null;

  /**
   * « Agir » — ids des décisions one-shot déjà jouées (vue du moteur).
   * Sert à calculer la réserve de redressements encore disponibles par
   * indicateur. Recalculé à chaque tour + au boot (déterministe au replay).
   */
  playedDecisions: string[];
  /**
   * « Agir » — redressement en cours : la décision affichée n'est pas issue
   * du fil normal mais d'une intervention sur un indicateur. Null hors
   * redressement. Non persisté : un reload revient au fil normal.
   */
  activeRemediation: { indicatorId: string; decisionId: string } | null;

  /**
   * La Bascule — id de la décision-opportunité exceptionnelle actuellement
   * proposée (bandeau opt-in), ou null. Recalculé par le moteur à chaque tour
   * (dérivé du seed) ; non persisté.
   */
  /** Contrat de campagne — promesses assumées (vue moteur, avec progrès). */
  pledges: PledgeView[];
  /** Promesses choisies dans l'overlay, en attente d'injection au
   *  prochain tour (elles voyagent dans les choices → replay-safe). */
  pendingPledges: string[];
  availableOpportunity: string | null;
  /** La Bascule — id de la bascule en cours de résolution (après saisie). */
  activeBascule: string | null;

  /**
   * Rapport « Ce tour-ci » — diff d'indicateurs du dernier tour joué
   * (sprint lisibilité 2026-06-10). Null au boot/replay : le rapport
   * décrit un tour qu'on vient de vivre, pas un état restauré.
   */
  lastTurnReport: TurnReport | null;

  /**
   * Erreur non fatale survenue pendant un tour (panic WASM, JSON
   * corrompu). La partie reste affichée — la save serveur est intacte,
   * un rechargement rejoue l'historique. Affichée en bannière dans le
   * cockpit, effaçable.
   */
  turnError: string | null;
  dismissTurnError: () => void;

  /** File des effets différés en attente — alimente le widget cockpit. */
  pendingEffects: PendingEffect[];

  /**
   * L5a — checkpoint actif (T13, T30, T46). Posé au tour où le moteur
   * émet une headline `BILAN ·`, lu par <CheckpointBanner>, vidé au
   * `dismissCheckpoint`. Pas persisté — c'est un événement éphémère.
   */
  activeCheckpoint: HeadlineEntry | null;
  dismissCheckpoint: () => void;

  boot: () => Promise<void>;
  currentDecision: () => Decision | null;
  pickOption: (optionId: string) => void;
  skipTurn: () => void;
  resetGame: () => Promise<void>;
  /** N3 — Réélection : reset partie en gardant le même archétype historique. */
  reelectGame: () => Promise<void>;
  openIndicator: (id: string) => void;
  closeIndicator: () => void;
  /** « Agir » — déclenche un redressement réservé pour un indicateur préoccupant. */
  actOnIndicator: (indicatorId: string) => void;
  /** « Agir » — abandonne le redressement en cours sans jouer de tour. */
  cancelRemediationFlow: () => void;
  /** La Bascule — saisit l'opportunité affichée (injecte la décision). */
  /** Contrat de campagne — enregistre le choix de l'overlay T5. */
  choosePledges: (ids: string[]) => void;
  seizeOpportunity: () => void;
  /** La Bascule — renonce à la bascule en cours (restaure le fil normal). */
  cancelBascule: () => void;
  /** J2 — appelé par PollOverlay à la fermeture pour figer la baseline. */
  markPollSeen: (state: WorldState) => void;
  /** Questionnaire beta — ouverture/fermeture de la modale de feedback. */
  feedbackOpen: boolean;
  openFeedback: () => void;
  closeFeedback: () => void;
}

interface PlanMeta {
  id: string;
  version: string;
}

interface ArchetypeMeta {
  id: string;
}

/** Rejoue `history` sur un engine frais et retourne l'état final + historique indicateurs. */
function replayHistory(
  engine: WasmEngine,
  history: string[][],
  initialState: WorldState,
): {
  state: WorldState;
  revelations: Revelation[];
  indicatorHistory: IndicatorHistory;
} {
  let state = initialState;
  let revelations: Revelation[] = [];
  let indicatorHistory = pushHistory(
    emptyHistory(),
    initialState,
    deriveAggregates(initialState),
  );
  for (const entry of history) {
    const result = advanceTurn(engine, entry);
    state = result.new_state;
    revelations = result.revelations;
    indicatorHistory = pushHistory(
      indicatorHistory,
      state,
      deriveAggregates(state),
    );
  }
  return { state, revelations, indicatorHistory };
}

function buildBlob(
  planId: string,
  planVersion: string,
  archetypeId: string,
  seed: number,
  history: string[][],
  presented: (string | null)[],
  turn: number,
  headlineFeed: HeadlineEntry[],
): SaveBlob {
  return {
    version: 1,
    plan_id: planId,
    plan_version: planVersion,
    archetype_id: archetypeId,
    seed,
    decisions_history: history,
    presented_history: presented,
    // V3 — sérialise les entrées enrichies. Les saves antérieures (string[])
    // sont migrées au boot via `coerceHeadlineFeed`.
    headline_feed: headlineFeed as unknown as string[],
    current_turn: turn,
  };
}

export const useGame = create<GameState>((set, get) => ({
  status: "idle",
  error: null,
  engine: null,
  worldState: null,
  lastRevelations: [],
  headlineFeed: INITIAL_HEADLINES,
  lastPollState: loadPollBaseline(),

  sessionId: null,
  planId: "",
  planVersion: "",
  archetypeId: "",
  seed: 42,

  decisions: [],
  decisionsHistory: [],
  presentedHistory: [],
  nextArchetypeId: null,
  selectorPhase: "scripted",

  indicatorHistory: emptyHistory(),
  lastTurnReport: null,
  turnError: null,
  activeIndicatorId: null,
  playedDecisions: [],
  activeRemediation: null,
  pledges: [],
  feedbackOpen: false,
  pendingPledges: [],
  availableOpportunity: null,
  activeBascule: null,
  pendingEffects: [],
  activeCheckpoint: null,

  dismissTurnError() {
    set({ turnError: null });
  },

  dismissCheckpoint() {
    set({ activeCheckpoint: null });
  },

  async boot() {
    if (get().status === "loading" || get().status === "ready") return;
    set({ status: "loading", error: null });
    try {
      const sessionId = getOrCreateSessionId();

      // N1 — point d'entrée archétype. On lit le choix persisté
      // (localStorage) ; si absent, social_cradle par défaut. Le
      // composant `ArchetypeSelector` se charge de la sélection
      // explicite avant de relancer boot() via `pickArchetype()`.
      const archetypePath = loadArchetypeFile();

      const [archetypeJson, planJson, decisionsJson, eventsJson, actorsJson] =
        await Promise.all([
          fetch(archetypePath).then((r) => r.text()),
          fetch("/data/plans/mvp_plan.json").then((r) => r.text()),
          // Catalogue embarqué dans le WASM (cf. crates/wasm/src/lib.rs::embedded_decisions_json).
          // Plus de fetch HTTP — réduit l'exposition publique du contenu.
          getEmbeddedDecisionsJson(),
          // Catalogue des événements L1-L4 — chargé pour activer les tirages
          // narratifs runtime (G3 — événements actifs).
          fetch("/data/events.json")
            .then((r) => r.text())
            .catch((err) => {
              console.warn("[events] échec du chargement des events", err);
              return "[]";
            }),
          // K1 — catalogue d'acteurs politiques (5 personae). Optionnel.
          fetch("/data/actors.json")
            .then((r) => r.text())
            .catch((err) => {
              console.warn("[actors] échec du chargement des actors", err);
              return "[]";
            }),
          loadTropes().catch((err) => {
            console.warn("[narratives] échec du chargement des tropes", err);
            return null;
          }),
        ]);

      const planMeta = JSON.parse(planJson) as PlanMeta;
      const archetypeMeta = JSON.parse(archetypeJson) as ArchetypeMeta;

      // N2 — détermine le seed avant l'engine. Si une save existe et
      // est compatible, on reprend son seed (déterminisme du replay).
      // Sinon nouvelle partie = nouveau seed = nouvelle séquence.
      const savedEarly = await fetchSave(sessionId);
      const sessionSeed: number =
        savedEarly &&
        savedEarly.plan_id === planMeta.id &&
        savedEarly.plan_version === planMeta.version &&
        savedEarly.archetype_id === archetypeMeta.id
          ? savedEarly.seed
          : generateSeed();

      // N2 — shuffle des symboliques scriptées par le seed pour que
      // mandat 2 ne propose pas exactement les mêmes 15 questions.
      const shuffledPlanJson = shuffleScriptedSymbolics(planJson, sessionSeed);

      const engine = await createEngine({
        archetypeJson,
        planJson: shuffledPlanJson,
        decisionsJson,
        seed: BigInt(sessionSeed),
      });
      // Charge le catalogue d'événements après la construction.
      try {
        engine.loadEvents(eventsJson);
      } catch (err) {
        console.warn("[events] échec loadEvents", err);
      }
      // K1 — charge les acteurs politiques.
      try {
        engine.loadActors(actorsJson);
      } catch (err) {
        console.warn("[actors] échec loadActors", err);
      }
      // Sprint Europe — charge le catalogue de procédures européennes
      // (EDP, surveillance dette, Article 7…). Embarqué dans le WASM
      // via include_str!.
      try {
        const euJson = await getEmbeddedEuropeanInterventionsJson();
        engine.loadEuropeanInterventions(euJson);
      } catch (err) {
        console.warn("[european] échec loadEuropeanInterventions", err);
      }
      const initialState = snapshot(engine);
      const decisions: Decision[] = JSON.parse(decisionsJson);

      // N2 — réutilise la save déjà fetchée pour décider le seed.
      const saved = savedEarly;
      const compatible =
        saved !== null &&
        saved.plan_id === planMeta.id &&
        saved.plan_version === planMeta.version &&
        saved.archetype_id === archetypeMeta.id &&
        saved.seed === sessionSeed;

      let finalState = initialState;
      let finalRevelations: Revelation[] = [];
      let finalIndicatorHistory = pushHistory(
        emptyHistory(),
        initialState,
        deriveAggregates(initialState),
      );
      let decisionsHistory: string[][] = [];
      // L'historique des suggestions démarre par celle de l'engine au boot
      // (tour 0). Elle sera potentiellement écrasée par la save.
      let presentedHistory: (string | null)[] = [suggestedNext(engine) ?? null];

      if (compatible && saved.decisions_history.length > 0) {
        // Robustesse (2026-06-10) : un id d'option disparu du catalogue
        // (contenu renommé/supprimé entre deux déploiements) faisait
        // échouer tout le boot → écran d'erreur terminal. On préfère
        // dégrader : save effacée, partie fraîche, le joueur rejoue.
        let replayed: ReturnType<typeof replayHistory>;
        try {
          replayed = replayHistory(
            engine,
            saved.decisions_history,
            initialState,
          );
        } catch (err) {
          console.warn(
            "[saves] replay impossible (contenu modifié ?) — reset",
            err,
          );
          deleteSave(sessionId);
          set({
            status: "error",
            error:
              "Votre sauvegarde n'est plus compatible avec cette version du jeu. " +
              "Elle a été effacée — rechargez la page pour démarrer un nouveau mandat.",
          });
          return;
        }
        finalState = replayed.state;
        finalRevelations = replayed.revelations;
        finalIndicatorHistory = replayed.indicatorHistory;
        decisionsHistory = saved.decisions_history;
        // Si la save porte l'historique des suggestions, on le restaure
        // tel quel — c'est la seule façon de figer la question perçue
        // par le joueur avant et après un refresh.
        if (saved.presented_history && saved.presented_history.length > 0) {
          presentedHistory = saved.presented_history;
        } else {
          // Rétro-compat : save ancienne sans presented_history. On
          // reconstruit en demandant à l'engine au fil du replay,
          // mais cette branche peut diverger — on la garde comme
          // best-effort.
          presentedHistory = [null];
          for (let i = 0; i <= decisionsHistory.length; i++) {
            presentedHistory.push(null);
          }
          presentedHistory = presentedHistory.slice(
            0,
            decisionsHistory.length + 1,
          );
          presentedHistory[presentedHistory.length - 1] =
            suggestedNext(engine) ?? null;
        }
      } else if (saved && !compatible) {
        console.warn("[saves] save incompatible — reset", {
          saved: {
            plan_id: saved.plan_id,
            plan_version: saved.plan_version,
            archetype_id: saved.archetype_id,
            seed: saved.seed,
          },
          current: {
            plan_id: planMeta.id,
            plan_version: planMeta.version,
            archetype_id: archetypeMeta.id,
            seed: sessionSeed,
          },
        });
        deleteSave(sessionId);
      }

      // La suggestion finale = dernière entrée de presentedHistory.
      // Elle prime sur `suggestedNext(engine)` pour figer visuellement
      // la question présentée avant/après refresh.
      const lastPresented =
        presentedHistory.length > 0
          ? presentedHistory[presentedHistory.length - 1]
          : (suggestedNext(engine) ?? null);

      // H4 — restaure le feed de presse cumulé si la save en porte un.
      // V3 — coerce les anciennes saves `string[]` vers `HeadlineEntry[]`.
      const finalHeadlineFeed: HeadlineEntry[] =
        compatible && saved.headline_feed && saved.headline_feed.length > 0
          ? coerceHeadlineFeed(saved.headline_feed)
          : INITIAL_HEADLINES;

      set({
        status: "ready",
        engine,
        worldState: finalState,
        sessionId,
        planId: planMeta.id,
        planVersion: planMeta.version,
        archetypeId: archetypeMeta.id,
        seed: sessionSeed,
        decisions,
        decisionsHistory,
        presentedHistory,
        nextArchetypeId: lastPresented,
        selectorPhase: selectorMode(engine),
        indicatorHistory: finalIndicatorHistory,
        lastRevelations: finalRevelations,
        pendingEffects: pendingEffects(engine),
        headlineFeed: finalHeadlineFeed,
        playedDecisions: playedDecisions(engine),
        activeRemediation: null,
        pledges: wasmPledges(engine),
        pendingPledges: [],
        availableOpportunity: wasmAvailableOpportunity(engine),
        activeBascule: null,
      });
      // Tracking : nouvelle partie démarrée (pas un resume de save existante).
      if (!compatible) {
        track(sessionId, "game_started", {
          archetype: archetypeMeta.id,
          plan_id: planMeta.id,
          seed: sessionSeed,
          ...(trafficSource ? { source: trafficSource } : {}),
        });
      }
    } catch (e) {
      set({
        status: "error",
        error: e instanceof Error ? e.message : String(e),
      });
    }
  },

  currentDecision() {
    const { decisions, nextArchetypeId } = get();
    if (!nextArchetypeId) return null;
    return decisions.find((d) => d.id === nextArchetypeId) ?? null;
  },

  pickOption(optionId) {
    const state = get();
    if (!state.engine || !state.sessionId) return;
    // Anti tour-fantôme (audit robustesse 2026-06-10) : un double-clic
    // rapide sur VALIDER rejouait l'option du tour précédent (le store a
    // déjà avancé, le re-render n'a pas encore désactivé le bouton).
    // On n'accepte que les options de la décision actuellement proposée.
    const current = state.currentDecision();
    if (!current || !current.options.some((o) => o.id === optionId)) {
      console.warn("[turn] option ignorée (décision déjà jouée)", optionId);
      return;
    }
    let result: ReturnType<typeof advanceTurn>;
    try {
      // Contrat de campagne — les promesses en attente voyagent dans les
      // choices du tour (interceptées par le moteur, rejouées au replay).
      result = advanceTurn(state.engine, [optionId, ...state.pendingPledges]);
    } catch (e) {
      // Panic WASM / moteur : on n'écrase PAS l'état affiché — la save
      // serveur est intacte, recharger rejoue la partie.
      set({ turnError: e instanceof Error ? e.message : String(e) });
      return;
    }
    const aggregates = deriveAggregates(result.new_state);
    const nextHistory = pushHistory(
      state.indicatorHistory,
      result.new_state,
      aggregates,
    );
    // Fix 2026-07-15 : les pledges DOIVENT être dans l'historique sauvegardé
    // (ils sont passés à advanceTurn ci-dessus) — sinon la promesse disparaît
    // silencieusement au rechargement (replay sans pledge_*).
    const decisionsHistory = [
      ...state.decisionsHistory,
      [optionId, ...state.pendingPledges],
    ];
    const nextSuggestion = suggestedNext(state.engine) ?? null;
    const presentedHistory = [...state.presentedHistory, nextSuggestion];
    const nextHeadlines = mergeEventsIntoHeadlines(
      result.events,
      result.headlines,
      state.headlineFeed,
    );
    // Checkpoint detection — BILAN T13/T30/T46 OU palier UE critique
    // (Article 7, fonds gelés, MES) qui apparaît ce tour.
    const checkpoint = detectCheckpoint(
      result.headlines,
      state.worldState?.polity.european_interventions,
      result.new_state.polity.european_interventions,
    );
    // Lisibilité — rapport « Ce tour-ci » : diff entre l'état d'avant
    // (encore dans le store) et le nouvel état.
    const turnReport = state.worldState
      ? buildTurnReport(
          state.worldState,
          result.new_state,
          state.decisions,
          optionId,
          result.events,
          result.event_impacts ?? [],
        )
      : null;
    set({
      worldState: result.new_state,
      lastRevelations: result.revelations,
      nextArchetypeId: nextSuggestion,
      selectorPhase: selectorMode(state.engine),
      indicatorHistory: nextHistory,
      decisionsHistory,
      presentedHistory,
      pendingEffects: pendingEffects(state.engine),
      headlineFeed: nextHeadlines,
      lastTurnReport: turnReport,
      turnError: null,
      playedDecisions: playedDecisions(state.engine),
      activeRemediation: null,
      pledges: wasmPledges(state.engine),
      pendingPledges: [],
      availableOpportunity: wasmAvailableOpportunity(state.engine),
      activeBascule: null,
      ...(checkpoint ? { activeCheckpoint: checkpoint } : {}),
    });
    pushSave(
      state.sessionId,
      buildBlob(
        state.planId,
        state.planVersion,
        state.archetypeId,
        state.seed,
        decisionsHistory,
        presentedHistory,
        result.new_state.turn,
        nextHeadlines,
      ),
    );
    // Tracking anonyme : question répondue (compte « questions répondues »).
    track(state.sessionId, "decision_made", {
      decision_id: current.id,
      option_id: optionId,
      turn: result.new_state.turn,
    });
    // Partie terminée ce tour-ci → envoyer le profil politique qui se dégage.
    if (
      result.new_state.polity.mandate_ended &&
      !state.worldState?.polity.mandate_ended
    ) {
      trackMandateEnded(
        state.sessionId,
        result.new_state,
        state.archetypeId,
        wasmPledges(state.engine),
      );
    }
  },

  skipTurn() {
    const state = get();
    if (!state.engine || !state.sessionId) return;
    let result: ReturnType<typeof advanceTurn>;
    try {
      result = advanceTurn(state.engine, [...state.pendingPledges]);
    } catch (e) {
      set({ turnError: e instanceof Error ? e.message : String(e) });
      return;
    }
    const aggregates = deriveAggregates(result.new_state);
    const nextHistory = pushHistory(
      state.indicatorHistory,
      result.new_state,
      aggregates,
    );
    // Fix 2026-07-15 : idem pickOption — les pledges signés pendant un tour
    // de pause doivent survivre au rechargement.
    const decisionsHistory = [
      ...state.decisionsHistory,
      [...state.pendingPledges],
    ];
    const nextSuggestion = suggestedNext(state.engine) ?? null;
    const presentedHistory = [...state.presentedHistory, nextSuggestion];
    const nextHeadlines = mergeEventsIntoHeadlines(
      result.events,
      result.headlines,
      state.headlineFeed,
    );
    // Checkpoint detection — BILAN T13/T30/T46 OU palier UE critique
    // (Article 7, fonds gelés, MES) qui apparaît ce tour.
    const checkpoint = detectCheckpoint(
      result.headlines,
      state.worldState?.polity.european_interventions,
      result.new_state.polity.european_interventions,
    );
    // Lisibilité — rapport « Ce tour-ci », même sur tour passé : la
    // dérive organique (dette, inflation) reste visible et datée.
    const turnReport = state.worldState
      ? buildTurnReport(
          state.worldState,
          result.new_state,
          state.decisions,
          null,
          result.events,
        )
      : null;
    set({
      worldState: result.new_state,
      lastRevelations: result.revelations,
      nextArchetypeId: nextSuggestion,
      selectorPhase: selectorMode(state.engine),
      indicatorHistory: nextHistory,
      decisionsHistory,
      presentedHistory,
      pendingEffects: pendingEffects(state.engine),
      headlineFeed: nextHeadlines,
      lastTurnReport: turnReport,
      turnError: null,
      playedDecisions: playedDecisions(state.engine),
      activeRemediation: null,
      pledges: wasmPledges(state.engine),
      pendingPledges: [],
      availableOpportunity: wasmAvailableOpportunity(state.engine),
      activeBascule: null,
      ...(checkpoint ? { activeCheckpoint: checkpoint } : {}),
    });
    pushSave(
      state.sessionId,
      buildBlob(
        state.planId,
        state.planVersion,
        state.archetypeId,
        state.seed,
        decisionsHistory,
        presentedHistory,
        result.new_state.turn,
        nextHeadlines,
      ),
    );
  },

  async resetGame() {
    // 2026-07-15 : on NE supprime plus la save de l'ancienne session —
    // clearSession() génère un nouvel id, l'ancienne ligne devient une
    // archive d'analyse playtest (déroulé + feed complet en base).
    clearSession();
    persistPollBaseline(null);
    // N1 — repartir avec un nouveau choix d'archétype au reset.
    try {
      localStorage.removeItem(ARCHETYPE_CHOICE_KEY);
      // Rejoue la cinématique d'investiture au prochain boot.
      localStorage.removeItem("mp_investiture_seen");
    } catch {
      /* ignore */
    }
    set({
      status: "idle",
      engine: null,
      worldState: null,
      lastRevelations: [],
      sessionId: null,
      decisions: [],
      decisionsHistory: [],
      presentedHistory: [],
      nextArchetypeId: null,
      selectorPhase: "scripted",
      indicatorHistory: emptyHistory(),
      lastTurnReport: null,
      turnError: null,
      activeIndicatorId: null,
      lastPollState: null,
      headlineFeed: INITIAL_HEADLINES,
    });
    // N1 — au reset, on ne re-boot pas tout de suite : App.tsx
    // détectera l'absence de choix d'archétype et affichera le
    // sélecteur. Le boot reprend après la sélection.
  },

  async reelectGame() {
    // N3 — Réélection : même chose que `resetGame` mais on conserve
    // le choix d'archétype (le contexte historique reste). Nouveau
    // seed = nouveau cheminement narratif, dans le même monde initial.
    // 2026-07-15 : save conservée (cf. resetGame) — archive d'analyse.
    clearSession();
    persistPollBaseline(null);
    // Reset les snapshots dérivés (profil T8, baselines tutoriel) pour
    // que le mandat suivant les recalcule proprement.
    try {
      localStorage.removeItem("mp.profileT8Snapshot");
      localStorage.removeItem("mp.profileCrystallized");
      [
        "mp.profileMidterm.20",
        "mp.profileMidterm.32",
        "mp.profileMidterm.44",
        "mp.profileMidterm.56",
      ].forEach((k) => localStorage.removeItem(k));
      localStorage.removeItem("mp.lastPollShownTurn");
    } catch {
      /* ignore */
    }
    set({
      status: "idle",
      engine: null,
      worldState: null,
      lastRevelations: [],
      sessionId: null,
      decisions: [],
      decisionsHistory: [],
      presentedHistory: [],
      nextArchetypeId: null,
      selectorPhase: "scripted",
      indicatorHistory: emptyHistory(),
      lastTurnReport: null,
      turnError: null,
      activeIndicatorId: null,
      lastPollState: null,
      headlineFeed: INITIAL_HEADLINES,
    });
    // Boot direct : l'archétype est déjà choisi, App.tsx ne montrera
    // pas le sélecteur.
    await get().boot();
  },

  openIndicator(id) {
    set({ activeIndicatorId: id });
  },
  closeIndicator() {
    set({ activeIndicatorId: null });
  },

  actOnIndicator(indicatorId) {
    const state = get();
    if (!state.engine) return;
    // Choisit le premier redressement encore disponible pour cet indicateur
    // (réservé, non encore consommé, et différent de celui déjà en cours).
    const candidate = state.decisions.find(
      (d) =>
        d.remediation_for === indicatorId &&
        !state.playedDecisions.includes(d.id) &&
        d.id !== state.activeRemediation?.decisionId,
    );
    if (!candidate) return;
    if (!injectRemediation(state.engine, candidate.id)) return;
    set({
      nextArchetypeId: suggestedNext(state.engine) ?? null,
      activeRemediation: { indicatorId, decisionId: candidate.id },
      activeIndicatorId: null,
    });
    // « Agir » se déclenche depuis le cockpit (sous la question) : le
    // redressement injecté devient la question, on remonte dessus pour que
    // le joueur la voie. rAF : après le commit React qui rend la carte.
    requestAnimationFrame(() => {
      document
        .querySelector(".problem")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  },

  cancelRemediationFlow() {
    const state = get();
    if (!state.engine || !state.activeRemediation) return;
    cancelRemediation(state.engine);
    set({
      nextArchetypeId: suggestedNext(state.engine) ?? null,
      activeRemediation: null,
    });
  },

  choosePledges(ids) {
    // 1 à 2 promesses ; injectées au prochain tour via les choices
    // (source de vérité replay). Si le joueur recharge avant le prochain
    // tour, l'overlay se représentera — cohérent.
    set({ pendingPledges: ids.slice(0, 2) });
  },

  seizeOpportunity() {
    const state = get();
    if (!state.engine || !state.availableOpportunity) return;
    const id = state.availableOpportunity;
    if (!wasmSeizeOpportunity(state.engine, id)) return;
    // La bascule devient la prochaine question (injectée en tête de file).
    set({
      nextArchetypeId: suggestedNext(state.engine) ?? null,
      activeBascule: id,
      availableOpportunity: null,
    });
  },

  cancelBascule() {
    const state = get();
    if (!state.engine || !state.activeBascule) return;
    // Réutilise le rail « Agir » : la saisie pose pending_redressement, donc
    // cancelRemediation restaure la décision normale. L'opportunité, elle, est
    // consommée (le moment est passé) — pas de retour en arrière.
    cancelRemediation(state.engine);
    set({
      nextArchetypeId: suggestedNext(state.engine) ?? null,
      activeBascule: null,
    });
  },

  markPollSeen(state) {
    persistPollBaseline(state);
    set({ lastPollState: state });
  },

  openFeedback() {
    set({ feedbackOpen: true });
  },
  closeFeedback() {
    set({ feedbackOpen: false });
  },
}));
